import React, { useEffect, useState } from "react";
import { BaseModal } from "@/components/ui/base-modal";
import { DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sticker, SquareArrowUpRight, BadgePercent } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "@/context/LanguageContext";
interface InviteEarnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export function InviteEarnDialog({ open, onOpenChange }: InviteEarnDialogProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, isRtl, formatNumber } = useTranslation();
  const [inviteCode, setInviteCode] = useState<string>("");
  const [referralCount, setReferralCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  
  const inviteLink = inviteCode 
    ? `https://shattaraai.com/invite/${inviteCode}` 
    : "Loading...";

  useEffect(() => {
    if (open && user) {
      fetchInviteData();
    }
  }, [open, user]);

  const fetchInviteData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Fetch user's invite code
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("invite_code")
        .eq("id", user.id)
        .single();

      if (profileError) throw profileError;
      
      if (profile?.invite_code) {
        setInviteCode(profile.invite_code);
        
        // Fetch referral count
        const { count, error: countError } = await supabase
          .from("referrals")
          .select("*", { count: "exact", head: true })
          .eq("referrer_id", user.id);

        if (countError) throw countError;
        setReferralCount(count || 0);
      }
    } catch (error) {
      console.error("Error fetching invite data:", error);
      toast.error("Failed to load invite code");
    } finally {
      setLoading(false);
    }
  };
  const handleCopyLink = async () => {
    if (!inviteCode || loading) return;
    
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success(t("referral.linkCopied"));
    } catch (error) {
      toast.error(t("referral.copyFailed"));
    }
  };
  return (
    <BaseModal
      open={open}
      onOpenChange={onOpenChange}
      title=""
      className="max-w-[95vw] md:max-w-lg border-none"
      contentClassName="p-6 flex flex-col gap-4 overflow-y-auto max-h-[95vh]"
    >
      <DialogTitle className="sr-only">Refer &amp; Earn</DialogTitle>

      {/* Banner Section */}
      <div className="flex flex-col space-y-1.5 text-left">
        <div className="relative h-fit overflow-hidden rounded-xl">
          <img src="/images/referral-dark-v2.png" alt="Referral" className="hidden w-full object-contain dark:block" />
          <img src="/images/referral-light-v2.png" alt="Referral" className="block w-full object-contain dark:hidden" />
        </div>
      </div>

      {/* How it works section */}
      <div className="md:py-2" dir={isRtl ? "rtl" : "ltr"}>
        <div className={`mb-3 text-base font-normal text-muted-foreground ${isRtl ? "text-right" : "text-left"}`}>
          {t("referral.howItWorks")}
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <SquareArrowUpRight className={`h-5 w-5 shrink-0 text-primary ${isRtl ? "scale-x-[-1]" : ""}`} />
            <span className={`text-base font-normal text-foreground ${isRtl ? "text-right" : "text-left"}`}>
              {t("referral.step1")}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Sticker className="h-5 w-5 shrink-0 text-primary" />
            <span className={`text-base font-normal text-foreground ${isRtl ? "text-right" : "text-left"}`}>
              {t("referral.step2")}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <BadgePercent className={`h-5 w-5 shrink-0 text-primary ${isRtl ? "scale-x-[-1]" : ""}`} />
            <span className={`text-base font-normal text-foreground ${isRtl ? "text-right" : "text-left"}`}>
              {t("referral.step3")}
            </span>
          </div>
        </div>
      </div>

      {/* Invite link section */}
      <div className="flex flex-col" dir={isRtl ? "rtl" : "ltr"}>
        <span className={`mb-3 flex items-center gap-4 text-base font-normal text-muted-foreground ${isRtl ? "flex-row-reverse pr-0 pl-2" : "pr-2"}`}>
          {t("referral.yourInviteLink")}
          <div className="h-0.5 flex-1 bg-border"></div>
          <span className="flex items-center gap-2 text-muted-foreground whitespace-nowrap">
            <span>
              {t("referral.usedByUsers").replace("{count}", loading ? "..." : formatNumber(referralCount))}
            </span>
          </span>
        </span>

        <div className={`flex flex-col items-center space-y-2 rounded-xl bg-muted p-2 sm:flex-row sm:space-y-0 ${isRtl ? "sm:flex-row-reverse" : ""}`}>
          <div className="flex w-full flex-1 items-center px-2">
            <input
              className={`flex h-9 w-full border-none shadow-none focus:outline-none focus:ring-0 focus-visible:ring-0 bg-transparent text-base md:text-sm text-foreground ${isRtl ? "text-right" : "text-left"}`}
              readOnly
              value={inviteLink}
              tabIndex={-1}
              dir="ltr"
            />
          </div>
          <Button
            onClick={handleCopyLink}
            disabled={loading || !inviteCode}
            className="h-8 px-4 py-2 w-full rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 sm:w-auto text-sm font-medium disabled:opacity-50 whitespace-nowrap"
          >
            {loading ? t("common.loading") : t("referral.copyLink")}
          </Button>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-center">
        <button
          onClick={() => navigate("/terms")}
          className="text-xs font-normal text-muted-foreground underline hover:underline underline-offset-4 transition-colors duration-100"
        >
          {t("referral.viewTerms")}
        </button>
      </div>
    </BaseModal>
  );
}
